/**
 * Calculates the perceived brightness of a color.
 * @param hex The hex color string (e.g., "#RRGGBB").
 * @returns A number between 0 (black) and 255 (white).
 */
function getLuminance(hex: string): number {
  if (!hex || hex.length < 4) return 255; // Default to white for invalid inputs

  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (r, g, b) => r + r + g + g + b + b);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return 255; // Default to white if parsing fails

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  // Formula for perceptive luminance (YIQ)
  return (r * 299 + g * 587 + b * 114) / 1000;
}

/**
 * Returns a contrasting text color (black or white) for a given background color.
 * @param hexColor The background color in hex format.
 * @returns '#FFFFFF' for dark backgrounds, '#000000' for light backgrounds.
 */
export function getContrastingTextColor(hexColor: string | undefined): string {
  if (!hexColor) return '#000000'; // Default to black if no color is provided
  
  // A threshold of 128 is a common choice for this calculation.
  return getLuminance(hexColor) > 128 ? '#000000' : '#FFFFFF';
}